<template>
  <card :title="title" no-footer no-padding>
    <b-dropdown slot="card-header-right" variant="link" no-caret right boundary="window">
      <i slot="button-content" class="icon-more" />

      <b-dropdown-item @click="exportCSV">{{ $t('kpi:exportCsv') | capitalize }}</b-dropdown-item>
    </b-dropdown>

    <template slot="card-body">
      <div v-show="dataProvider && dataProvider.length === 0" class="text-center text-muted font-italic p-2">{{ $t('kpi:noData') | capitalize }}</div>

      <div v-show="dataProvider && dataProvider.length > 0">
        <!-- Less than 15 channels / SF > display as nav pills -->
        <ul v-if="groupByValues.length <= 15" class="nav nav-pills mx-3 mt-3">
          <li v-for="groupByValue in groupByValues" :key="groupByValue" class="nav-item">
            <a :class="{active: groupByValue===groupBySelectedValue}" href="#" class="nav-link px-2 py-1" @click.prevent="groupBySelectedValue=groupByValue">{{ groupByValue + (kpi.groupBy === 'frq' ? ' MHz' : '') }}</a>
          </li>
        </ul>

        <!-- More than 15 channels / SF > display as list -->
        <form v-else class="form-inline px-3 pt-3">
          <div class="form-group">
            <label class="mr-2" for="groupBySelectedValue">{{ $t('kpi:groupBy' + kpi.groupBy) | capitalize }}:</label>
            <select id="groupBySelectedValue" v-model="groupBySelectedValue" class="form-control px-1">
              <option v-for="groupByValue in groupByValues" :key="groupByValue" :value="groupByValue">{{ groupByValue + (kpi.groupBy === 'frq' ? ' MHz' : '') }}</option>
            </select>
          </div>
        </form>

        <div :id="id" class="chart mx-3" />
      </div>
    </template>
  </card>
</template>

<script>
import { groupBy, orderBy, keys, each, upperFirst, extend, isArray, camelCase } from 'lodash-es'
import constants from '@/configuration/constants'
import format from 'date-fns/format'
import formatDuration from 'vue-kerlink/dist/filters/duration'
import formatDate from 'vue-kerlink/dist/filters/date'
import '@/modules/amcharts'

import BDropdown from 'bootstrap-vue/es/components/dropdown/dropdown'
import BDropdownItem from 'bootstrap-vue/es/components/dropdown/dropdown-item'

const filledAttrs = ['systemDiskUsed', 'userDiskUsed']

export default {
  components: { BDropdown, BDropdownItem },

  props: {
    id: {
      type: String,
      required: true
    },

    title: {
      type: String,
      default: ''
    },

    // configuration of the kpi
    kpi: {
      type: Object,
      required: true
    },

    // lable of the left axis
    leftAxisTitle: {
      type: String,
      default: ''
    },

    // temporal unit
    temporalUnit: {
      type: String,
      default: 'HOUR'
    },

    // data of the series, is undefined while data are not loaded
    dataProvider: {
      type: Array,
      default: () => { return [] }
    },

    // entity id for export
    entityId: {
      type: String,
      required: true
    },

    // raw data for export
    rawData: {
      type: Array,
      default: () => { return [] }
    }
  },

  data () {
    return {
      groupBySelectedValue: null
    }
  },

  computed: {
    // dataProvider grouped by the groupBy attribute
    groupByData () {
      if (this.kpi.groupBy) {
        return groupBy(this.dataProvider, data => data[this.kpi.groupBy])
      }
      return this.dataProvider
    },

    // Values of the attribute use to group by
    groupByValues () {
      if (this.kpi.groupBy) {
        return orderBy(keys(this.groupByData))
      }
      return []
    }
  },

  watch: {
    groupBySelectedValue (groupBySelectedValue) {
      if (this.dataProvider && this.dataProvider.length > 0) {
        this.buildChart()
      }
    },

    dataProvider (dataProvider) {
      this.groupBySelectedValue = null

      if (dataProvider && dataProvider.length > 0) {
        this.buildChart()
      }
    }
  },

  methods: {
    // Get dataProvider according to groupBy attribute/value
    buildDataProvider () {
      if (this.kpi.groupBy) {
        if (!this.groupBySelectedValue) {
          this.groupBySelectedValue = this.groupByValues[0]
        }
        return this.groupByData[this.groupBySelectedValue]
      }
      return this.dataProvider
    },

    // Format data to use unit
    formateValue (value, graph, space = ' ') {
      let unit = graph.unit

      if (graph.unit === 'ms') {
        // [h]h [m]m [s]s
        return formatDuration(value, 'compact').replace(' ', space)
      } else {
        const defUnits = [
          { key: 'unitTera', mod: 1000000000000 },
          { key: 'unitGega', mod: 1000000000 },
          { key: 'unitMega', mod: 1000000 },
          { key: 'unitKilo', mod: 1000 }
        ]
        each(defUnits, (defUnit) => {
          if (graph[defUnit.key] && value - defUnit.mod >= 0) {
            value /= defUnit.mod
            unit = graph[defUnit.key]
            return false
          }
        })
        return this.$utils.formatNumber(value) + (unit ? space + unit : '')
      }
    },

    // Build graphs using kpi properties
    buildGraphs () {
      const graphs = []

      each(this.kpi.attrs, attr => {
        // Check the first data, if the value for this attr is null, remove this attr
        const dataProvider = this.buildDataProvider()
        if (dataProvider[0][attr] === null) return false

        const category = this.kpi.category.toLowerCase()

        if (this.kpi.chartType === 'bars') {
          // Format dataProvider and get attributes to display (Set to manage duplicate keys)
          const keys = new Set()
          each(dataProvider, (data) => {
            each(data[attr], (value, key) => {
              data[key] = value
              keys.add(key)
              delete data[attr]
            })
          })

          // Column chart
          for (let key of keys) {
            const termGraph = {
              title: upperFirst(this.$t(`kpi:kpiList.${category}.${key}`)),
              valueField: key,
              unit: this.kpi.unit,
              connect: this.kpi.connect || false,
              type: 'column',
              fillAlphas: 0.8,
              lineAlpha: 0.3,
              balloonFunction: (data) => {
                return upperFirst(this.$t(`kpi:kpiList.${category}.${key}`)) + ': ' + this.formateValue(data.values.value, data.graph)
              }
            }

            graphs.push(termGraph)
          }
        } else if (this.kpi.link === 'gatewayConnections') {
          // Connections chart
          const graph = {
            title: upperFirst(this.$t(`kpi:kpiList.${category}.${attr}`)),
            valueField: attr,
            valueAxis: 'status',
            markerType: 'square',
            lineAlpha: 0,
            fillColors: attr === 'connected' ? constants.colors.green : constants.colors.orange,
            fillAlphas: 0.5,
            balloonFunction: (data) => {
              let bearer = this.$t('g-properties:' + data.dataContext.networkBearer)
              return data.values.value === 1 ? upperFirst(this.$t(`kpi:kpiList.${category}.${attr}`)) + ` (${bearer})` : ''
            }
          }

          graphs.push(graph)
        } else {
          const title = upperFirst(category === 'gateway' ? this.$t(`kpi:kpiList.${category}.${attr}`) : this.$t(`${this.kpi.name}_${attr}`))

          // Line chart
          const graph = {
            title,
            valueField: attr,
            unit: this.kpi.unit,
            bullet: 'round',
            bulletSize: '4',
            connect: this.kpi.connect || false,
            fillAlphas: filledAttrs.indexOf(attr) !== -1 ? 0.5 : 0,
            balloonFunction: (data) => {
              if (data.values) return title + ': ' + this.formateValue(data.values.value, data.graph, '&nbsp;')
              return ''
            }
          }

          // Set colors for threshold and hysteresis
          if (attr.toLowerCase().indexOf('threshold') !== -1) {
            graph.lineColor = constants.colors.red
          }
          if (attr.toLowerCase().indexOf('hysteresis') !== -1) {
            graph.lineColor = constants.colors.cyan
          }

          graphs.push(graph)
        }
      })

      // Add alarms chart
      if (this.kpi.alarms) {
        graphs.push({
          id: 'alarms',
          title: upperFirst(this.$t('kpi:alarms')),
          valueField: 'alarmThreshold',
          bulletSizeField: 'alarmBulletSize',
          unit: this.kpi.unit,
          balloonFunction: data => {
            if (!data.dataContext.alarms) return ''
            // Display list of alarms, or number of alarms if > 3
            if (data.dataContext.alarms.length > 3) return this.$t('kpi:kpiList.gateway.alarm', { count: data.dataContext.alarms.length })

            let balloon = ''
            each(data.dataContext.alarms, (alarm) => {
              balloon += formatDate(alarm.createTimeStamp) + ' (' + this.formateValue(alarm.value, data.graph, '&nbsp;') + ')<br>'
            })
            return balloon
          },
          bullet: 'circle',
          bulletColor: constants.colors.red,
          fillColors: constants.colors.red,
          lineAlpha: 0,
          valueAxis: 'alarm'
        })
      }

      return graphs
    },

    // Build chart
    buildChart () {
      if (this.dataProvider.length <= 0) return

      // default category field
      let categoryField = 'time'
      if (!this.dataProvider[0][categoryField]) {
        categoryField = 'timestamp'
        if (!this.dataProvider[0][categoryField]) {
          console.error('No time field found to create chart.')
        }
      }

      // Y axe configuration
      let valueAxes = [{
        position: 'left',
        title: this.leftAxisTitle,
        labelFunction: (value) => {
          return this.formateValue(value, extend({}, this.kpi))
        }
      }]
      if (this.kpi.unit === '%') {
        valueAxes[0].minimum = 0
        valueAxes[0].maximum = 100
      }
      // Status axe for connections chart
      valueAxes.push({
        id: 'status',
        position: 'left',
        maximum: 1,
        minimum: 0,
        labelFunction: () => ''
      })

      // default legend
      const legend = {
        useGraphSettings: true,
        valueAlign: 'left',
        valueFunction: (data) => {
          if (data.graph && data.graph.id === 'alarms') return isArray(data.dataContext.alarms) ? data.dataContext.alarms.length : 0
          if (this.kpi.link !== 'gatewayConnections' && data.values) return this.formateValue(data.values.value, data.graph)
          return ''
        }
      }

      // Format dates
      let categoryBalloonDateFormat = 'JJ:NN, DD MMMM YYYY'
      let minPeriod = 'hh'
      switch (this.temporalUnit) {
        case 'MINUTE':
          categoryBalloonDateFormat = 'JJ:NN, DD MMMM YYYY'
          minPeriod = 'mm'
          break
        case 'DAY':
          categoryBalloonDateFormat = 'DD MMMM YYYY'
          minPeriod = 'DD'
          break
        case 'WEEK':
          categoryBalloonDateFormat = ' (W) DD MMMM, YYYY'
          minPeriod = 'WW'
          break
        case 'MONTH':
          categoryBalloonDateFormat = 'MMMM YYYY'
          minPeriod = 'MM'
          break
        case 'YEAR':
          categoryBalloonDateFormat = 'YYYY'
          minPeriod = 'YYYY'
          break
      }

      window.AmCharts.makeChart(this.id, {
        type: 'serial',
        theme: 'none',
        dataProvider: this.buildDataProvider(),
        valueAxes,
        graphs: this.buildGraphs(),
        legend,
        chartScrollbar: {
          scrollbarHeight: 50,
          backgroundAlpha: 0,
          selectedBackgroundAlpha: 0.1,
          selectedBackgroundColor: constants.colors['gray-600'],
          graphFillAlpha: 0,
          graphLineAlpha: 0.5,
          selectedGraphFillAlpha: 0,
          selectedGraphLineAlpha: 1,
          autoGridCount: true,
          color: constants.colors['gray-500']
        },
        chartCursor: {
          categoryBalloonDateFormat,
          cursorColor: constants.colors.blue
        },
        categoryField,
        categoryAxis: {
          minPeriod: this.kpi.link === 'gatewayConnections' ? 'ss' : minPeriod,
          parseDates: true,
          minorGridEnabled: true
        },
        export: {
          enabled: false
        }
      })
    },

    exportCSV () {
      if (this.rawData) {
        const filename = this.entityId + '-' +
          camelCase(this.title) + '-' +
          (this.kpi.link !== 'gatewayConnections' ? this.temporalUnit.toLowerCase() + '-' : '') +
          format(Date.now(), 'YYYYMMDD-hhmm')

        this.$utils.exportCsv(this.rawData, filename)
      }
    }
  }
}
</script>

<style scoped lang="scss">
  .chart {
    height: 350px;
  }

  .legend {
    position: relative;
  }
</style>
